import { Options, SeriesOptionsType, TooltipFormatterContextObject } from 'highcharts';
import { IChartSeries, IChartSeriesData } from '@/interfaces/storageForecasting/IRunOutput.ts';
import { IFilter } from '@/interfaces/storageForecasting/IFilter.ts';
import cssVariables from '@/assets/cssVariables.json';
import { getTooltipTableRow, getTooltipWrapper } from '@/helpers/charts/chartSettings.ts';
import { addMonths, addYears } from '../dateUtils.ts';

function getSeriesByYear (data: number[], yearOptions: string[], pointStart: Date): Record<number, Array<number>> {
    let series: Record<number, Array<number>> = {};
    const startDate = new Date(pointStart);
    let sliceEnd = 12 - startDate.getMonth();
    let sliceStart = 0;

    yearOptions.forEach((fullYear, index) => {
        series = {
            ...series,
            [fullYear]: data.slice(sliceStart, sliceEnd),
        };
        if (index === 0) {
            sliceStart = sliceEnd;
        } else sliceStart += 12;
        sliceEnd += 12;
    });

    return series;
}

function getYearlist (pointStart: Date, pointEnd: Date): string[] {
    const years = [];
    let startDate = new Date(pointStart);
    const endDate = new Date(pointEnd);

    while (startDate < endDate) {
        years.push(startDate.getFullYear().toString());
        startDate = addYears(startDate, 1);
    }
    return years;
}

function generateChartSeriesData (chartData: IChartSeries, filters: IFilter, yearOptions: string[], pointStart: Date): Array<IChartSeriesData> {
    let localChartData: Array<IChartSeriesData> = [];

    if (filters.period.id === 'monthly') {
        localChartData = chartData.data.map(series => ({
            ...series,
            dataByYear: getSeriesByYear(series.data, yearOptions, pointStart),
        }));

        const index = Number(filters.yearStarting.id);

        const chartSeries = localChartData.map((series: IChartSeriesData) => ({
            name: series.name,
            color: series.color || null,
            data: series.dataByYear ? series.dataByYear[index] : series.data,
        }));

        return chartSeries;
    }

    const chartSeries = chartData.data.map(series => ({
        name: series.name,
        data: series.data,
        color: series.color || null,
    }));

    return chartSeries;
}

function generateChartCategories (filters: IFilter, pointStart: Date, pointEnd: Date): string[] {
    let yearStarting = new Date(filters.yearStarting.id);
    const startDate = new Date(pointStart);
    let endDate = new Date(pointEnd);
    const dates: string[] = [];

    if (filters.period.id === 'monthly') {
        if (yearStarting.getFullYear() === startDate.getFullYear()) {
            yearStarting = startDate;
        }

        const year = new Date(yearStarting);
        endDate = addYears(year, 1);

        while (yearStarting < endDate) {
            dates.push(`${yearStarting.getMonth() + 1}/${yearStarting.getFullYear()}`);
            yearStarting = addMonths(yearStarting, 1);
        }
    }

    if (filters.period.id === 'calendarYearly') {
        const startYear = startDate.getFullYear();
        const endYear = endDate.getFullYear();

        for (let i = startYear; i <= endYear; i += 1) {
            dates.push(`${i}`);
        }
    }

    if (filters.period.id === 'quarterly') {
        const startYear = startDate.getFullYear();
        const endYear = endDate.getFullYear();

        for (let i = startYear; i <= endYear; i += 1) {
            if (i === startDate.getFullYear()) {
                const quarter = Math.ceil((startDate.getMonth() + 1) / 3);
                for (let j = quarter; j <= 4; j += 1) {
                    dates.push(`${startDate.getFullYear()} Q${j}`);
                }
            } else {
                dates.push(`${i} Q1`);
                dates.push(`${i} Q2`);
                dates.push(`${i} Q3`);
                dates.push(`${i} Q4`);
            }
        }
    }

    if (filters.period.id === 'financialYearly') {
        const startYear = startDate.getMonth() >= 3 ? startDate.getFullYear() : startDate.getFullYear() - 1;
        const endYear = endDate.getMonth() >= 3 ? endDate.getFullYear() : endDate.getFullYear() - 1;

        for (let i = startYear; i <= endYear; i += 1) {
            const financialYearStart = new Date(i, 3, 1);
            const financialYearEnd = new Date(i + 1, 2, 31);

            if (financialYearStart <= endDate && financialYearEnd > startDate) {
                dates.push(`${i}-${i + 1}`);
            }
        }
    }
    return dates;
}

function getPrefixUnit (chartOptions: Options): string | null {
    if (chartOptions.yAxis) {
        let axis = chartOptions.yAxis;
        if (Array.isArray(axis)) axis = axis[0];
        if (axis.title && axis.title.text) {
            const prefixSplit = axis.title.text.split('(');
            if (prefixSplit.length > 1) {
                return prefixSplit[0];
            }
        }
    }
    return null;
}

function getSuffixUnit (chartOptions: Options): string | null {
    if (chartOptions.yAxis) {
        let axis = chartOptions.yAxis;
        if (Array.isArray(axis)) axis = axis[0];
        if (axis.title && axis.title.text) {
            const prefixSplit = axis.title.text.split('/');
            if (prefixSplit.length > 1) {
                return `/${prefixSplit[1]}`;
            }
            return axis.title.text;
        }
    }
    return null;
}

function tooltipFormatter (this: TooltipFormatterContextObject): string {
    let rows: string = '';
    this.points?.forEach((point: TooltipFormatterContextObject) => {
        const y = ((point?.y || 0).toFixed(2).toString().replace(/\B(?=(\d{3})+(?!\d))/g, ','));
        const prefix = getPrefixUnit(point.series.chart.options);
        const suffix = getSuffixUnit(point.series.chart.options);
        rows += getTooltipTableRow({
            label: point.point.series.name,
            value: `${prefix ?? ''}${y}${suffix ? ` ${suffix}` : ''}`,
            color: point.color,
        });
    });
    return getTooltipWrapper({
        content: rows,
        contentType: 'table',
        header: `${this.x}`,
    });
}

function generateChartOptions (
    data: IChartSeries,
    filters: IFilter,
    yearOptions: string[],
    pointStart: Date,
    pointEnd: Date,
    chartName: string,
    type: string,
    min?: number,
    max?: number,
): Options {
    const flatData = data.data.flatMap(x => x.data);
    const isPositive = flatData.every(y => typeof y === 'number' && y >= 0);
    const chartData: Options = {
        chart: {
            type,
            animation: false,
            backgroundColor: 'transparent',
            style: {
                fontFamily: 'Montserrat,sans-serif',
            },
            spacingTop: 16,
        },
        legend: {
            borderRadius: 6,
            borderWidth: 0,
            bubbleLegend: {
                enabled: false,
                borderWidth: 1,
                connectorDistance: 40,
                ranges: [{ color: 'rgba(233, 63, 111, 0.7)' }, { color: 'rgba(247, 166, 0, 0.7)' }, { color: 'rgba(0, 163, 199, 0.7)' }],
            },
            enabled: true,
            itemStyle: {
                fontSize: cssVariables.fonts.extraSmallFontSize,
                fontWeight: 'normal',
            },
            itemDistance: 10,
            x: 10,
            maxHeight: 60,
            margin: 0,
        },
        title: {
            text: chartName,
            style: {
                fontSize: '16px',
            },
        },
        tooltip: {
            formatter: tooltipFormatter,
        },
        xAxis: {
            categories: generateChartCategories(filters, pointStart, pointEnd),
            labels: {
                rotation: 0,
                autoRotation: [0],
            },
        },
        yAxis: {
            title: {
                style: {
                    fontSize: '12px',
                },
                text: data.unit,
                margin: 10,
            },
            min: min ?? (isPositive ? 0 : null),
            max: max ?? null,
        },
        plotOptions: {
            column: {
                borderWidth: 0,
                stacking: 'normal',
                dataLabels: {
                    enabled: false,
                },
                groupPadding: 0,
                pointPadding: 0.1,
            },
        },
        series: generateChartSeriesData(data, filters, yearOptions, pointStart) as Array<SeriesOptionsType>,
    };

    return chartData;
}

export {
    getSeriesByYear,
    getYearlist,
    getPrefixUnit,
    getSuffixUnit,
    generateChartOptions,
    generateChartSeriesData,
    generateChartCategories,
    tooltipFormatter as storageForecastTooltipFormatter,
};
